import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { imageListSchema, imageSchema, type Image, type UploadResult } from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TextField } from '../../components/TextField';
import styles from '../competitions/Competitions.module.css';
import { uploadInBatches } from '../competitions/upload';
import { errorMessage } from '../../i18n/errors';
import { ImageViewer } from '../../components/ImageViewer';
import viewerStyles from '../../components/ImageViewer.module.css';
import { MergeDialog } from './MergeDialog';

/**
 * Bilddatenbank: Raster der eigenen Bilder mit Suche, Filter „ohne Einreichung", Upload (ohne
 * Wettbewerb) und Löschen unbenutzter Bilder.
 */
export function ImagesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [unused, setUnused] = useState(false);
  const [results, setResults] = useState<UploadResult[]>([]);
  const [toDelete, setToDelete] = useState<Image | null>(null);
  const [toMerge, setToMerge] = useState<Image | null>(null);
  const [viewIndex, setViewIndex] = useState<number | null>(null);
  const attachInput = useRef<HTMLInputElement>(null);
  const [attachFor, setAttachFor] = useState<Image | null>(null);

  const search = new URLSearchParams({ limit: '200' });
  if (q) search.set('q', q);
  if (unused) search.set('unused', 'true');
  const images = useQuery({
    queryKey: ['images', 'list', search.toString()],
    queryFn: () => apiRequest('GET', `/images?${search.toString()}`, { schema: imageListSchema }),
    placeholderData: keepPreviousData,
  });
  const upload = useMutation({
    mutationFn: (files: File[]) =>
      uploadInBatches(
        '/images',
        files.map((file) => ({ file, category: '' })),
        setResults,
      ),
    onSuccess: async (all) => {
      setResults(all);
      await queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });
  const attach = useMutation({
    mutationFn: ({ image, file }: { image: Image; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return apiRequest('POST', `/images/${image.id}/file`, { form, schema: imageSchema });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['images'] }),
  });
  const remove = useMutation({
    mutationFn: (image: Image) => apiRequest('DELETE', `/images/${image.id}`),
    onSuccess: async () => {
      setToDelete(null);
      await queryClient.invalidateQueries({ queryKey: ['images'] });
    },
  });

  /** Ordnet die gewählte Datei dem Bild zu, für das der Dialog geöffnet wurde. */
  function onAttach(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file && attachFor) attach.mutate({ image: attachFor, file });
  }

  /** Startet den Upload der gewählten Dateien. */
  function onPick(event: ChangeEvent<HTMLInputElement>): void {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (files.length > 0) upload.mutate(files);
  }

  return (
    <section>
      <div className={styles.header}>
        <h1>{t('images.title')}</h1>
        <div className={styles.actions}>
          <input
            ref={input}
            className={styles.hidden}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            aria-label={t('images.upload')}
            onChange={onPick}
          />
          <Button disabled={upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? t('competitions.uploading') : t('images.upload')}
          </Button>
        </div>
      </div>
      <div className={styles.toolbar} role="search">
        <TextField
          label={t('images.search')}
          type="search"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={unused}
            onChange={(event) => setUnused(event.target.checked)}
          />
          {t('images.unusedOnly')}
        </label>
      </div>
      {results.length > 0 && (
        <ul aria-label={t('competitions.uploadResults')}>
          {results.map((result, index) => (
            <li key={`${result.filename}-${index}`}>
              {result.filename}:{' '}
              {result.status === 'error' ? (
                <span className={styles.error}>
                  {t(`errors.${result.code}`, { defaultValue: t('errors.unknown') })}
                </span>
              ) : (
                <span className={styles.success}>
                  {t(`competitions.uploadStatus.${result.status}`)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {images.isPending && <p role="status">{t('common.loading')}</p>}
      {images.isError && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, images.error)}
        </p>
      )}
      {images.data && (
        <>
          <p role="status">
            {images.data.total === 0
              ? t('images.empty')
              : t('images.count', { count: images.data.total })}
          </p>
          <ul className={styles.grid}>
            {images.data.items.map((image) => (
              <li key={image.id} className={styles.tile}>
                {image.hasThumb ? (
                  <button
                    type="button"
                    className={viewerStyles.thumbButton}
                    aria-label={t('viewer.open', { name: image.title })}
                    onClick={() => setViewIndex(images.data.items.indexOf(image))}
                  >
                    <img
                      className={styles.tileImage}
                      src={apiUrl(`/images/${image.id}/thumb`)}
                      alt=""
                      loading="lazy"
                    />
                  </button>
                ) : (
                  <span className={styles.tileImage} />
                )}
                <span className={styles.tileTitle}>{image.title}</span>
                <span className={styles.meta}>
                  <span>{t('images.submissions', { count: image.usage.submissions })}</span>
                  {image.usage.accepted > 0 && (
                    <Badge tone="success">
                      {t('images.accepted', { count: image.usage.accepted })}
                    </Badge>
                  )}
                </span>
                {/* Nur für Titeleinträge ganz ohne Bild; Altbestandsbilder haben ein Vorschaubild und brauchen kein Original. */}
                {!image.hasThumb && (
                  <Button
                    variant="secondary"
                    aria-label={t('images.attachFor', { name: image.title })}
                    disabled={attach.isPending}
                    onClick={() => {
                      setAttachFor(image);
                      attachInput.current?.click();
                    }}
                  >
                    {t('images.attach')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  aria-label={t('images.mergeFor', { name: image.title })}
                  onClick={() => setToMerge(image)}
                >
                  {t('images.merge')}
                </Button>
                {image.usage.submissions === 0 && (
                  <Button
                    variant="secondary"
                    aria-label={t('images.deleteImage', { name: image.title })}
                    onClick={() => setToDelete(image)}
                  >
                    {t('common.delete')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
      {(upload.error || remove.error || attach.error) && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, upload.error ?? remove.error ?? attach.error)}
        </p>
      )}
      <input
        ref={attachInput}
        className={styles.hidden}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        aria-label={t('images.attachInput')}
        onChange={onAttach}
      />
      <ImageViewer
        items={(images.data?.items ?? []).map((image) => ({ id: image.id, title: image.title }))}
        index={viewIndex}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
      />
      <MergeDialog source={toMerge} onClose={() => setToMerge(null)} />
      <ConfirmDialog
        title={t('images.deleteTitle')}
        message={t('images.deleteMessage', { name: toDelete?.title ?? '' })}
        confirmLabel={t('common.delete')}
        isOpen={toDelete !== null}
        busy={remove.isPending}
        onConfirm={() => toDelete && remove.mutate(toDelete)}
        onCancel={() => setToDelete(null)}
      />
    </section>
  );
}
